import type jsPDF from "jspdf";

import jetBrainsMonoBoldUrl from "@/assets/fonts/JetBrainsMono-Bold.ttf?url";
import jetBrainsMonoRegularUrl from "@/assets/fonts/JetBrainsMono-Regular.ttf?url";
import outfitRegularUrl from "@/assets/fonts/Outfit-Regular.ttf?url";
import outfitSemiBoldUrl from "@/assets/fonts/Outfit-SemiBold.ttf?url";
import logoPineUrl from "@/assets/svg/logo-pine.svg";

/**
 * The brand the downloadable receipt is drawn in.
 *
 * **These values are the transactional-email palette of `coloca-nest`, not the dashboard's tokens**, and the
 * difference is deliberate. A comprobante is a document: it is read in a PDF viewer, printed, and forwarded
 * next to the emails Coloca already sends, so it belongs to the same family as those — not to the screen it
 * was downloaded from. The six current templates there each declare an identical `COLORS` block (see
 * `src/integrations/resend/templates/verify-email/render.ts`), and this mirrors it.
 *
 * Two consequences worth stating, because they look like mistakes otherwise:
 *
 * - There is **no success colour**. `brand` doubles as the positive tone, exactly as the emails do.
 * - The dashboard's own `--primary` is `oklch(0.508 0.118 165.612)`, which is a hair off `#00735C`. The
 *   document uses the committed email hex rather than a conversion of the token: a PDF has no `oklch`, no
 *   dark mode and no colour-space negotiation, so the literal is the honest value. `PayinQrCard` already
 *   hardcodes the same `#00735C` for the same reason.
 */
export const RECEIPT_COLORS = {
  /** Page background and the footer band. */
  page: "#EDEFE5",
  /** Header band and the tinted row groups. */
  surface: "#F4F6EC",
  /** The document shell and the inner cards. */
  card: "#FFFFFF",
  /** Headings and figures. */
  ink: "#1F2A26",
  /** Body copy and row values. */
  body: "#3F4D47",
  /** Row labels, mono eyebrows, fine print. */
  muted: "#6F7A75",
  /** Hairlines, dividers, the shell border. */
  line: "#D9DDD2",
  /** The brand green. Buttons in the emails; accents and the settled stamp here. */
  brand: "#00735C",
  /** The emails' `mossSoft` — the soft green behind an icon tile there, behind the settled band here. */
  brandSoft: "#E2F3EA",
  warningFill: "#FBF1DA",
  warningLine: "#E9C97A",
  warningInk: "#7A5A18",
  dangerFill: "#FCE7E3",
  dangerLine: "#E9B0A3",
  dangerInk: "#8A2418",
} as const;

/**
 * Font names as registered in the jsPDF VFS. They are NOT the app's Inter + Geist Mono, for the same reason
 * the palette is not the app's: the emails set Outfit and JetBrains Mono, and the receipt reads as a sibling
 * of those. See `src/assets/fonts/README.md`.
 */
export const RECEIPT_FONTS = { sans: "Outfit", mono: "JetBrainsMono" } as const;

/** Fallbacks when the TTFs cannot be fetched — jsPDF's built-ins, which need no network. */
export const RECEIPT_FALLBACK_FONTS = { sans: "helvetica", mono: "courier" } as const;

/** 8198 × 1401, the intrinsic size of `logo-pine.svg`. */
export const RECEIPT_LOGO_ASPECT = 8198 / 1401;

/**
 * The issuing entity's registered address, as the transactional emails print it
 * (`src/integrations/resend/templates/new-device/copies.ts` in `coloca-nest`).
 *
 * Not an i18n key, and that is the point: a legal name and a street address are the same three lines in
 * Spanish, English and Portuguese, and putting them through the translation files would invite three
 * divergent versions of one registered address.
 */
export const RECEIPT_ISSUER_ADDRESS = [
  "Coloca Payments S.A. de C.V.",
  "Av. Paseo de la Reforma 296, Piso 21",
  "Cuauhtémoc, 06600, CDMX, México",
] as const;

interface ReceiptFontPayload {
  sansRegular: string;
  sansSemiBold: string;
  monoRegular: string;
  monoBold: string;
}

/**
 * Base64 of a font file, chunked on the way through `String.fromCharCode`. Spreading a 112 kB `Uint8Array`
 * into it overflows the argument limit and throws, which is the kind of failure that only shows up on the
 * largest of the four files.
 */
const fetchFontBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Receipt font ${url} answered ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  const CHUNK = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
};

/**
 * Resolved once per session and held in memory. The four TTFs are static assets rather than bundled base64,
 * so nobody who never downloads a receipt pays for them; the first download fetches ~320 kB and every later
 * one is free.
 *
 * Only a SUCCESSFUL load is cached. Caching the rejection would turn one offline moment into a session where
 * every receipt silently falls back to Helvetica.
 */
let fontPayload: Promise<ReceiptFontPayload> | null = null;

const loadReceiptFonts = (): Promise<ReceiptFontPayload> => {
  fontPayload ??= Promise.all([
    fetchFontBase64(outfitRegularUrl),
    fetchFontBase64(outfitSemiBoldUrl),
    fetchFontBase64(jetBrainsMonoRegularUrl),
    fetchFontBase64(jetBrainsMonoBoldUrl),
  ])
    .then(([sansRegular, sansSemiBold, monoRegular, monoBold]) => ({
      sansRegular,
      sansSemiBold,
      monoRegular,
      monoBold,
    }))
    .catch((error: unknown) => {
      fontPayload = null;
      throw error;
    });
  return fontPayload;
};

/**
 * Registers Outfit and JetBrains Mono on this document.
 *
 * Returns whether it worked, and the caller is expected to carry on either way: a receipt set in Helvetica is
 * a receipt, and refusing to produce the document because a font file 404'd would withhold the one thing the
 * person actually asked for. The weights are registered under the `normal` / `bold` style slots jsPDF
 * switches with `setFont`, so the renderer never names a weight.
 */
export const registerReceiptFonts = async (doc: jsPDF): Promise<boolean> => {
  try {
    const payload = await loadReceiptFonts();
    doc.addFileToVFS("Outfit-Regular.ttf", payload.sansRegular);
    doc.addFont("Outfit-Regular.ttf", RECEIPT_FONTS.sans, "normal");
    doc.addFileToVFS("Outfit-SemiBold.ttf", payload.sansSemiBold);
    doc.addFont("Outfit-SemiBold.ttf", RECEIPT_FONTS.sans, "bold");
    doc.addFileToVFS("JetBrainsMono-Regular.ttf", payload.monoRegular);
    doc.addFont("JetBrainsMono-Regular.ttf", RECEIPT_FONTS.mono, "normal");
    doc.addFileToVFS("JetBrainsMono-Bold.ttf", payload.monoBold);
    doc.addFont("JetBrainsMono-Bold.ttf", RECEIPT_FONTS.mono, "bold");
    return true;
  } catch {
    return false;
  }
};

let logoDataUrl: Promise<string> | null = null;

/**
 * The Coloca wordmark as a PNG data URL, rasterized from the SAME `logo-pine.svg` the app renders.
 *
 * JsPDF cannot place an SVG, so something has to raster it. Rasterizing at runtime rather than committing a
 * PNG is what keeps the document from drifting: `coloca-nest` generated its email logo from this very file,
 * and the PDF that preceded this one used `logo-black.png` — a different, older asset — which is exactly how
 * a comprobante ends up carrying a logo the product stopped using.
 *
 * Drawn at 1400 px wide, ~4× its printed size, so it stays crisp at 300 dpi and when a reader zooms in.
 */
export const receiptLogoPng = (): Promise<string> => {
  logoDataUrl ??= (async () => {
    const image = new Image();
    image.src = logoPineUrl;
    // `logo-pine.svg` carries explicit width/height attributes, without which an SVG has no intrinsic size
    // to decode against in WebKit and this resolves to a blank canvas.
    await image.decode();
    const width = 1400;
    const height = Math.round(width / RECEIPT_LOGO_ASPECT);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Receipt logo: no 2d context");
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL("image/png");
  })().catch((error: unknown) => {
    logoDataUrl = null;
    throw error;
  });
  return logoDataUrl;
};
